import { Fragment, jsx, jsxs } from "react/jsx-runtime";
import {
  Box,
  EMPTY_ARRAY,
  Group2d,
  Rectangle2d,
  ShapeUtil,
  Vec,
  WeakCache,
  exhaustiveSwitchError,
  getColorValue,
  getFontsFromRichText,
  isEqual,
  lerp,
  noteShapeMigrations,
  noteShapeProps,
  resizeScaled,
  resolveLineHeightPx,
  rng,
  toRichText,
  useColorMode,
  useEditor,
  useValue
} from "@tldraw/editor";
import { useCallback, useContext } from "react";
import { startEditingShapeWithRichText } from "../../tools/SelectTool/selectHelpers.mjs";
import { TldrawUiTooltip } from "../../ui/components/primitives/TldrawUiTooltip.mjs";
import { TranslationsContext } from "../../ui/hooks/useTranslation/useTranslation.mjs";
import {
  isEditingRichTextList,
  isEmptyRichText,
  renderHtmlFromRichTextForMeasurement,
  renderPlaintextFromRichText
} from "../../utils/text/richText.mjs";
import { isRightToLeftLanguage } from "../../utils/text/text.mjs";
import {
  LABEL_FONT_SIZES,
  LABEL_PADDING,
  TEXT_PROPS,
  getFontFamily
} from "../shared/default-shape-constants.mjs";
import { DefaultFontFaces, getThemeFontFaces } from "../shared/defaultFonts.mjs";
import { getDisplayValues } from "../shared/getDisplayValues.mjs";
import { HyperlinkButton } from "../shared/HyperlinkButton.mjs";
import { RichTextLabel, RichTextSVG } from "../shared/RichTextLabel.mjs";
import { useIsReadyForEditing } from "../shared/useEditablePlainText.mjs";
import { useEfficientZoomThreshold } from "../shared/useEfficientZoomThreshold.mjs";
import { CLONE_HANDLE_MARGIN, getNoteShapeForAdjacentPosition } from "./noteHelpers.mjs";
const NOTE_SHAPE_HORIZONTAL_ALIGNS = Object.freeze({
  start: "start",
  middle: "center",
  end: "end",
  "start-legacy": "start",
  "end-legacy": "end",
  "middle-legacy": "center"
});
const NOTE_SHAPE_VERTICAL_ALIGNS = Object.freeze({
  start: "start",
  middle: "middle",
  end: "end"
});
class NoteShapeUtil extends ShapeUtil {
  static type = "note";
  static props = noteShapeProps;
  static migrations = noteShapeMigrations;
  options = {
    resizeMode: "none",
    getDefaultDisplayValues(_editor, shape, theme, colorMode) {
      const { color, labelColor, font, size, align, verticalAlign } = shape.props;
      const colors = theme.colors[colorMode];
      return {
        noteWidth: 200,
        noteHeight: 200,
        noteBackgroundColor: getColorValue(colors, color, "noteFill"),
        borderColor: colors.noteBorder,
        borderWidth: 2,
        labelColor: labelColor === "black" ? getColorValue(colors, color, "noteText") : getColorValue(colors, labelColor, "fill"),
        labelFontFamily: getFontFamily(theme, font),
        labelFontSize: theme.fontSize * LABEL_FONT_SIZES[size],
        labelLineHeight: theme.lineHeight,
        labelFontWeight: TEXT_PROPS.fontWeight,
        labelFontVariant: TEXT_PROPS.fontVariant,
        labelFontStyle: TEXT_PROPS.fontStyle,
        labelPadding: LABEL_PADDING,
        labelHorizontalAlign: NOTE_SHAPE_HORIZONTAL_ALIGNS[align],
        labelVerticalAlign: NOTE_SHAPE_VERTICAL_ALIGNS[verticalAlign]
      };
    },
    getCustomDisplayValues() {
      return {};
    }
  };
  canEdit(shape) {
    return true;
  }
  hideResizeHandles(shape) {
    const { resizeMode } = this.options;
    switch (resizeMode) {
      case "none": {
        return true;
      }
      case "scale": {
        return false;
      }
      default: {
        throw exhaustiveSwitchError(resizeMode);
      }
    }
  }
  isAspectRatioLocked(shape) {
    return this.options.resizeMode === "scale";
  }
  hideSelectionBoundsFg(shape) {
    return false;
  }
  getDefaultProps() {
    return {
      color: "black",
      richText: toRichText(""),
      size: "m",
      font: "draw",
      align: "middle",
      verticalAlign: "middle",
      labelColor: "black",
      growY: 0,
      fontSizeAdjustment: 1,
      url: "",
      scale: 1,
      textLastEditedBy: null
    };
  }
  getGeometry(shape) {
    const { labelHeight, labelWidth } = this.getLabelSize(shape);
    const { scale } = shape.props;
    const dv = getDisplayValues(this, shape);
    const lh = labelHeight * scale;
    const lw = labelWidth * scale;
    const nw = dv.noteWidth * scale;
    const nh = getNoteHeight(shape, dv.noteHeight);
    return new Group2d({
      children: [
        new Rectangle2d({ width: nw, height: nh, isFilled: true }),
        new Rectangle2d({
          x: dv.labelHorizontalAlign === "start" ? 0 : dv.labelHorizontalAlign === "end" ? nw - lw : (nw - lw) / 2,
          y: dv.labelVerticalAlign === "start" ? 0 : dv.labelVerticalAlign === "end" ? nh - lh : (nh - lh) / 2,
          width: lw,
          height: lh,
          isFilled: true,
          isLabel: true,
          excludeFromShapeBounds: true
        })
      ]
    });
  }
  getHandles(shape) {
    const { scale } = shape.props;
    const isCoarsePointer = this.editor.getInstanceState().isCoarsePointer;
    if (isCoarsePointer) return [];
    const zoom = this.editor.getEfficientZoomLevel();
    if (zoom * scale < 0.25) return [];
    const dv = getDisplayValues(this, shape);
    const nh = getNoteHeight(shape, dv.noteHeight);
    const nw = dv.noteWidth * scale;
    const offset = CLONE_HANDLE_MARGIN / zoom * scale;
    if (zoom * scale < 0.5) {
      return [
        {
          id: "bottom",
          index: "a3",
          type: "clone",
          x: nw / 2,
          y: nh + offset
        }
      ];
    }
    return [
      {
        id: "top",
        index: "a1",
        type: "clone",
        x: nw / 2,
        y: -offset
      },
      {
        id: "right",
        index: "a2",
        type: "clone",
        x: nw + offset,
        y: nh / 2
      },
      {
        id: "bottom",
        index: "a3",
        type: "clone",
        x: nw / 2,
        y: nh + offset
      },
      {
        id: "left",
        index: "a4",
        type: "clone",
        x: -offset,
        y: nh / 2
      }
    ];
  }
  onResize(shape, info) {
    const { resizeMode } = this.options;
    switch (resizeMode) {
      case "none": {
        return void 0;
      }
      case "scale": {
        return resizeScaled(shape, info);
      }
      default: {
        throw exhaustiveSwitchError(resizeMode);
      }
    }
  }
  getText(shape) {
    return renderPlaintextFromRichText(this.editor, shape.props.richText);
  }
  getReferencedUserIds(shape) {
    return shape.props.textLastEditedBy ? [shape.props.textLastEditedBy] : [];
  }
  getFontFaces(shape) {
    const fonts = isEmptyRichText(shape.props.richText) ? [] : getFontsFromRichText(this.editor, shape.props.richText, {
      family: `tldraw_${shape.props.font}`,
      weight: "normal",
      style: "normal"
    });
    if (shape.props.textLastEditedBy && !isEmptyRichText(shape.props.richText)) {
      return [...fonts, DefaultFontFaces.tldraw_sans.normal.normal];
    }
    const themeFaces = getThemeFontFaces(this.editor.getCurrentTheme(), shape.props.font);
    if (themeFaces) return [...themeFaces, ...fonts];
    return fonts.length ? fonts : EMPTY_ARRAY;
  }
  component(shape) {
    const { id, type, props } = shape;
    const { scale, richText, fontSizeAdjustment, textLastEditedBy } = props;
    const handleKeyDown = useNoteKeydownHandler(id);
    const rotation = useValue(
      "shape rotation",
      () => this.editor.getShapePageTransform(id)?.rotation() ?? 0,
      [this.editor]
    );
    const colorMode = useColorMode();
    const dv = getDisplayValues(this, shape, colorMode);
    const nw = dv.noteWidth * scale;
    const nh = getNoteHeight(shape, dv.noteHeight);
    const hideShadows = useEfficientZoomThreshold(0.25 / scale);
    const isSelected = shape.id === this.editor.getOnlySelectedShapeId();
    const isReadyForEditing = useIsReadyForEditing(this.editor, shape.id);
    const isEmpty = isEmptyRichText(richText);
    const attribution = useValue(
      "attribution",
      () => {
        if (!textLastEditedBy || isEmpty) return null;
        const name = this.editor.getAttributionDisplayName(textLastEditedBy);
        if (!name) return null;
        return { short: name.split(" ")[0], full: name };
      },
      [textLastEditedBy, isEmpty, this.editor]
    );
    return /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsxs(
        "div",
        {
          id,
          className: "tl-note__container",
          style: {
            width: nw,
            height: nh,
            backgroundColor: dv.noteBackgroundColor,
            borderBottom: hideShadows ? `${dv.borderWidth * scale}px solid ${dv.borderColor}` : "none",
            boxShadow: hideShadows ? "none" : getNoteShadow(shape.id, rotation, scale)
          },
          children: [
            attribution && /* @__PURE__ */ jsx(TldrawUiTooltip, { content: attribution.full, side: "bottom", children: /* @__PURE__ */ jsx(
              "div",
              {
                className: "tl-note__attribution",
                style: {
                  ["--note-attribution-scale"]: scale,
                  fontSize: 11 * scale,
                  color: dv.labelColor,
                  opacity: 0.6
                },
                children: attribution.short
              }
            ) }),
            (isSelected || isReadyForEditing || !isEmpty) && /* @__PURE__ */ jsx(
              RichTextLabel,
              {
                shapeId: id,
                type,
                fontFamily: dv.labelFontFamily,
                fontSize: (fontSizeAdjustment ?? 1) * dv.labelFontSize,
                lineHeight: dv.labelLineHeight,
                textAlign: dv.labelHorizontalAlign,
                verticalAlign: dv.labelVerticalAlign,
                richText,
                isSelected,
                labelColor: dv.labelColor,
                wrap: true,
                padding: dv.labelPadding,
                hasCustomTabBehavior: true,
                showTextOutline: false,
                onKeyDown: handleKeyDown,
                style: scale !== 1 ? {
                  transform: `scale(${scale})`,
                  transformOrigin: "top left",
                  width: dv.noteWidth,
                  height: dv.noteHeight + shape.props.growY
                } : void 0
              }
            )
          ]
        }
      ),
      "url" in shape.props && shape.props.url && /* @__PURE__ */ jsx(HyperlinkButton, { url: shape.props.url })
    ] });
  }
  getIndicatorPath(shape) {
    const { scale } = shape.props;
    const dv = getDisplayValues(this, shape);
    const path = new Path2D();
    path.rect(0, 0, dv.noteWidth * scale, getNoteHeight(shape, dv.noteHeight));
    return path;
  }
  toSvg(shape, ctx) {
    const dv = getDisplayValues(this, shape, ctx.colorMode);
    const bounds = new Box(0, 0, dv.noteWidth, dv.noteHeight + shape.props.growY);
    const filterId = `note-shadow-${shape.id.replace(/:/g, "_")}`;
    ctx.addExportDef({
      key: filterId,
      getElement: () => /* @__PURE__ */ jsxs("filter", { id: filterId, x: "-10%", y: "-10%", width: "130%", height: "150%", children: [
        /* @__PURE__ */ jsx("feMorphology", { in: "SourceAlpha", operator: "erode", radius: "3", result: "erode1" }),
        /* @__PURE__ */ jsx("feGaussianBlur", { in: "erode1", stdDeviation: "3", result: "blur1" }),
        /* @__PURE__ */ jsx("feOffset", { in: "blur1", dy: "3", result: "offsetBlur1" }),
        /* @__PURE__ */ jsx("feComponentTransfer", { in: "offsetBlur1", result: "shadow1", children: /* @__PURE__ */ jsx("feFuncA", { type: "linear", slope: "0.5" }) }),
        /* @__PURE__ */ jsx("feMorphology", { in: "SourceAlpha", operator: "erode", radius: "10", result: "erode2" }),
        /* @__PURE__ */ jsx("feGaussianBlur", { in: "erode2", stdDeviation: "6", result: "blur2" }),
        /* @__PURE__ */ jsx("feOffset", { in: "blur2", dy: "6", result: "offsetBlur2" }),
        /* @__PURE__ */ jsx("feComponentTransfer", { in: "offsetBlur2", result: "shadow2", children: /* @__PURE__ */ jsx("feFuncA", { type: "linear", slope: "0.5" }) }),
        /* @__PURE__ */ jsxs("feMerge", { children: [
          /* @__PURE__ */ jsx("feMergeNode", { in: "shadow1" }),
          /* @__PURE__ */ jsx("feMergeNode", { in: "shadow2" }),
          /* @__PURE__ */ jsx("feMergeNode", { in: "SourceGraphic" })
        ] })
      ] })
    });
    const { textLastEditedBy } = shape.props;
    const attributionName = textLastEditedBy && !isEmptyRichText(shape.props.richText) ? this.editor.getAttributionDisplayName(textLastEditedBy)?.split(" ")[0] ?? null : null;
    return /* @__PURE__ */ jsxs(Fragment, { children: [
      ctx.isDarkMode ? null : /* @__PURE__ */ jsx(
        "rect",
        {
          rx: 1,
          width: dv.noteWidth,
          height: bounds.h,
          fill: dv.noteBackgroundColor,
          filter: `url(#${filterId})`
        }
      ),
      /* @__PURE__ */ jsx("rect", { rx: 1, width: dv.noteWidth, height: bounds.h, fill: dv.noteBackgroundColor }),
      /* @__PURE__ */ jsx(
        RichTextSVG,
        {
          fontSize: (shape.props.fontSizeAdjustment ?? 1) * dv.labelFontSize,
          fontFamily: dv.labelFontFamily,
          lineHeight: dv.labelLineHeight,
          textAlign: dv.labelHorizontalAlign,
          verticalAlign: dv.labelVerticalAlign,
          richText: shape.props.richText,
          labelColor: dv.labelColor,
          bounds,
          padding: dv.labelPadding,
          showTextOutline: false
        }
      ),
      attributionName && /* @__PURE__ */ jsx(
        "foreignObject",
        {
          x: 0,
          y: 0,
          width: dv.noteWidth,
          height: bounds.h,
          className: "tl-export-embed-styles",
          children: /* @__PURE__ */ jsx("div", { style: { position: "relative", width: "100%", height: "100%" }, children: /* @__PURE__ */ jsx(
            "div",
            {
              className: "tl-note__attribution",
              style: {
                fontSize: 11,
                color: dv.labelColor,
                opacity: 0.6
              },
              children: attributionName
            }
          ) })
        }
      )
    ] });
  }
  onBeforeCreate(next) {
    return this.getNoteSizeAdjustments(next);
  }
  onBeforeDuplicate(_source, duplicate) {
    if (isEmptyRichText(duplicate.props.richText)) return;
    return {
      ...duplicate,
      props: { ...duplicate.props, textLastEditedBy: this.editor.getAttributionUserId() }
    };
  }
  onBeforeUpdate(prev, next) {
    const richTextChanged = !isEqual(prev.props.richText, next.props.richText);
    if (!richTextChanged && prev.props.font === next.props.font && prev.props.size === next.props.size) {
      return;
    }
    let shape = next;
    if (richTextChanged) {
      if (isEmptyRichText(next.props.richText)) {
        shape = {
          ...shape,
          props: { ...shape.props, textLastEditedBy: null }
        };
      } else {
        shape = {
          ...shape,
          props: { ...shape.props, textLastEditedBy: this.editor.getAttributionUserId() }
        };
      }
    }
    return this.getNoteSizeAdjustments(shape) ?? (richTextChanged ? shape : void 0);
  }
  getInterpolatedProps(startShape, endShape, t) {
    return {
      ...(t > 0.5 ? endShape.props : startShape.props),
      scale: lerp(startShape.props.scale, endShape.props.scale, t)
    };
  }
  /**
   * Get the growY and fontSizeAdjustment for a shape.
   */
  getNoteSizeAdjustments(shape) {
    const dv = getDisplayValues(this, shape);
    const { labelHeight, fontSizeAdjustment } = this.getLabelSize(shape);
    const growY = Math.max(0, labelHeight - dv.noteHeight);
    if (growY !== shape.props.growY || fontSizeAdjustment !== shape.props.fontSizeAdjustment) {
      return {
        ...shape,
        props: {
          ...shape.props,
          growY,
          fontSizeAdjustment
        }
      };
    }
    return void 0;
  }
  _labelSizesForNoteCache = new WeakCache();
  /**
   * Get the cached label size for the shape.
   */
  getLabelSize(shape) {
    return this._labelSizesForNoteCache.get(shape, () => this.measureNoteLabelSize(shape));
  }
  /**
   * Expensively measure the label size for a note shape.
   */
  measureNoteLabelSize(shape) {
    const dv = getDisplayValues(this, shape);
    const { richText } = shape.props;
    if (isEmptyRichText(richText)) {
      const minHeight = resolveLineHeightPx(dv.labelFontSize, dv.labelLineHeight) + dv.labelPadding * 2;
      return { labelHeight: minHeight, labelWidth: 100, fontSizeAdjustment: 1 };
    }
    const unadjustedFontSize = dv.labelFontSize;
    let fontSizeAdjustment = unadjustedFontSize;
    let iterations = 0;
    let labelHeight = dv.noteHeight;
    let labelWidth = dv.noteWidth;
    const FUZZ = 1;
    do {
      fontSizeAdjustment = Math.min(unadjustedFontSize, unadjustedFontSize - iterations);
      const html = renderHtmlFromRichTextForMeasurement(this.editor, richText);
      const nextTextSize = this.editor.textMeasure.measureHtml(html, {
        ...TEXT_PROPS,
        lineHeight: dv.labelLineHeight,
        fontFamily: dv.labelFontFamily,
        fontSize: fontSizeAdjustment,
        maxWidth: dv.noteWidth - dv.labelPadding * 2 - FUZZ,
        disableOverflowWrapBreaking: true,
        measureScrollWidth: true
      });
      labelHeight = nextTextSize.h + dv.labelPadding * 2;
      labelWidth = nextTextSize.w + dv.labelPadding * 2;
      if (fontSizeAdjustment <= 14) {
        const html2 = renderHtmlFromRichTextForMeasurement(this.editor, richText);
        const nextTextSizeWithOverflowBreak = this.editor.textMeasure.measureHtml(html2, {
          ...TEXT_PROPS,
          lineHeight: dv.labelLineHeight,
          fontFamily: dv.labelFontFamily,
          fontSize: fontSizeAdjustment,
          maxWidth: dv.noteWidth - dv.labelPadding * 2 - FUZZ
        });
        labelHeight = nextTextSizeWithOverflowBreak.h + dv.labelPadding * 2;
        labelWidth = nextTextSizeWithOverflowBreak.w + dv.labelPadding * 2;
        break;
      }
      if (nextTextSize.scrollWidth.toFixed(0) === nextTextSize.w.toFixed(0)) {
        break;
      }
    } while (iterations++ < 50);
    return {
      labelHeight,
      labelWidth,
      fontSizeAdjustment: fontSizeAdjustment === unadjustedFontSize ? 1 : fontSizeAdjustment / unadjustedFontSize
    };
  }
}
function useNoteKeydownHandler(id) {
  const editor = useEditor();
  const translation = useContext(TranslationsContext);
  return useCallback(
    (e) => {
      const shape = editor.getShape(id);
      if (!shape) return;
      const isTab = e.key === "Tab";
      const isCmdEnter = (e.metaKey || e.ctrlKey) && e.key === "Enter";
      if (isTab && isEditingRichTextList(editor)) {
        e.preventDefault();
        return;
      }
      if (isTab || isCmdEnter) {
        e.preventDefault();
        const pageTransform = editor.getShapePageTransform(id);
        const pageRotation = pageTransform.rotation();
        const isRTL = !!(translation?.dir === "rtl" || // todo: can we check a partial of the text, so that we don't have to render the whole thing?
        isRightToLeftLanguage(renderPlaintextFromRichText(editor, shape.props.richText)));
        const noteUtil = editor.getShapeUtil(shape);
        const dv = getDisplayValues(noteUtil, shape);
        const noteOffset = isTab ? dv.noteWidth + editor.options.adjacentShapeMargin : dv.noteHeight + editor.options.adjacentShapeMargin + // If we're growing down, we need to account for the current shape's growY
        (isCmdEnter && !e.shiftKey ? shape.props.growY : 0);
        const offsetLength = noteOffset * shape.props.scale;
        const adjacentCenter = new Vec(
          isTab ? e.shiftKey != isRTL ? -1 : 1 : 0,
          isCmdEnter ? e.shiftKey ? -1 : 1 : 0
        ).mul(offsetLength).add(new Vec(dv.noteWidth / 2, dv.noteHeight / 2).mul(shape.props.scale)).rot(pageRotation).add(pageTransform.point());
        const newNote = getNoteShapeForAdjacentPosition(editor, {
          shape,
          center: adjacentCenter,
          pageRotation,
          noteWidth: dv.noteWidth,
          noteHeight: dv.noteHeight
        });
        if (newNote) {
          startEditingShapeWithRichText(editor, newNote, { selectAll: true });
        }
      }
    },
    [id, editor, translation?.dir]
  );
}
function getNoteHeight(shape, noteHeight) {
  return (noteHeight + shape.props.growY) * shape.props.scale;
}
function getNoteShadow(id, rotation, scale) {
  const random = rng(id);
  const lift = Math.abs(random()) + 0.5;
  const oy = Math.cos(rotation);
  const a = 5 * scale;
  const b = 4 * scale;
  const c = 6 * scale;
  const d = 7 * scale;
  return `0px ${Math.max(0, a - lift)}px ${a}px -${a}px rgba(15, 23, 31, .6),
	0px ${(b + lift * d) * Math.max(0, oy)}px ${c + lift * d}px -${b + lift * c}px rgba(15, 23, 31, ${(0.3 + lift * 0.1).toFixed(2)}),
	0px ${48 * scale}px ${10 * scale}px -${10 * scale}px inset rgba(15, 23, 44, ${((0.022 + random() * 5e-3) * ((1 + oy) / 2)).toFixed(2)})`;
}
export {
  NoteShapeUtil
};
//# sourceMappingURL=NoteShapeUtil.mjs.map
